import { SupabaseClient } from '@supabase/supabase-js';
import { DocumentRepository } from '../repositories/document.repository';
import { ProjectUserRepository } from '../repositories/project-user.repository';
import { DocumentDTO } from '../dtos';
import { DocumentMapper } from '../mappers/document.mapper';

export interface ImportNotionRequest {
  notionPageId: string;
  projectId: string;
  notionApiKey?: string; // Optional for backward compatibility
}

export interface ImportNotionResponse {
  success: boolean;
  document?: DocumentDTO;
  message?: string;
  error?: string;
}

export interface NotionPage {
  id: string;
  title: string;
  url: string;
  lastEditedTime: string;
  createdTime: string;
  icon?: {
    type: 'emoji' | 'external' | 'file';
    emoji?: string;
    external?: { url: string };
    file?: { url: string };
  };
  cover?: {
    type: 'external' | 'file';
    external?: { url: string };
    file?: { url: string };
  };
  parent?: {
    type: 'database_id' | 'page_id' | 'workspace';
    database_id?: string;
    page_id?: string;
    workspace?: boolean;
  };
}

export interface SearchNotionPagesRequest {
  notionApiKey?: string; // Optional - will use OAuth token if not provided
  projectId: string;
  query?: string;
  filter?: {
    property?: 'object';
    value?: 'page' | 'database';
  };
  sort?: {
    direction: 'ascending' | 'descending';
    timestamp: 'last_edited_time';
  };
  limit?: number;
}

export interface SearchNotionPagesResponse {
  success: boolean;
  pages?: NotionPage[];
  hasMore?: boolean;
  nextCursor?: string;
  error?: string;
}

export interface NotionIntegration {
  id: string;
  userId: string;
  projectId: string;
  accessToken: string;
  workspaceName?: string;
  workspaceIcon?: string;
  createdAt: string;
  updatedAt: string;
}

export interface NotionOAuthResponse {
  success: boolean;
  authUrl?: string;
  integration?: {
    id: string;
    workspace_name?: string;
    workspace_icon?: string;
    created_at: string;
  };
  message?: string;
  error?: string;
}

export class NotionService {
  private documentRepository: DocumentRepository;
  private projectUserRepository: ProjectUserRepository;

  constructor(private supabase: SupabaseClient) {
    this.documentRepository = new DocumentRepository(supabase);
    this.projectUserRepository = new ProjectUserRepository(supabase);
  }

  /**
   * 노션 페이지를 문서로 가져오기
   * @deprecated 노션 가져오기는 이제 notion-oauth 함수의 세션 모드를 통해 처리됩니다
   */
  async importFromNotion(
    request: ImportNotionRequest,
    userId: string
  ): Promise<ImportNotionResponse> {
    return {
      success: false,
      error: 'This method is deprecated. Use Notion OAuth session mode for importing.'
    };
  }

  /**
   * 노션 페이지 URL에서 페이지 ID 추출
   */
  extractPageIdFromUrl(url: string): string | null {
    try {
      // 다양한 노션 URL 형식 처리
      // https://www.notion.so/Page-Title-xxxxxxxxxxxxxxxxxxxxxxxxxxxxx
      // https://notion.so/workspace/Page-Title-xxxxxxxxxxxxxxxxxxxxxxxxxxxxx?v=yyy
      const match = url.match(/([a-f0-9]{32}|[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})/);
      return match ? match[1].replace(/-/g, '') : null;
    } catch {
      return null;
    }
  }

  /**
   * 노션 API 키 유효성 검증 (선택적)
   */
  validateNotionApiKey(apiKey: string): boolean {
    // 노션 API 키는 'secret_'로 시작하고 50자 정도의 길이를 가짐
    return /^secret_[a-zA-Z0-9]{43,}$/.test(apiKey);
  }

  /**
   * 노션 페이지 검색
   * @deprecated 노션 검색은 더 이상 지원되지 않습니다
   */
  async searchNotionPages(
    request: SearchNotionPagesRequest,
    userId: string
  ): Promise<SearchNotionPagesResponse> {
    return {
      success: false,
      error: 'This method is deprecated. Notion search is no longer supported.'
    };
  }

  /**
   * 노션 페이지에서 제목 추출
   */
  private extractPageTitle(page: any): string {
    try {
      // properties에서 title 타입 찾기
      const titleProperty = Object.values(page.properties || {}).find(
        (prop: any) => prop.type === 'title'
      ) as any;

      if (titleProperty?.title?.length > 0) {
        return titleProperty.title
          .map((text: any) => text.plain_text)
          .join('');
      }

      return 'Untitled';
    } catch {
      return 'Untitled';
    }
  }

  /**
   * 노션 OAuth 인증 시작
   */
  async initiateNotionOAuth(
    userId: string,
    projectId: string
  ): Promise<NotionOAuthResponse> {
    try {
      // Get user JWT token
      const { data: { session } } = await this.supabase.auth.getSession();
      if (!session?.access_token) {
        return {
          success: false,
          error: 'Authentication required - no valid session found'
        };
      }

      const { data, error } = await this.supabase.functions.invoke('notion-oauth', {
        body: {
          action: 'authorize',
          projectId,
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        return {
          success: false,
          error: error.message || '노션 OAuth 시작에 실패했습니다.'
        };
      }

      return {
        success: true,
        authUrl: data.authUrl,
      };
    } catch (error) {
      console.error('Error initiating Notion OAuth:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : '노션 OAuth 시작에 실패했습니다.'
      };
    }
  }

  /**
   * 노션 OAuth 콜백 처리
   */
  async handleNotionOAuthCallback(
    code: string,
    state: string
  ): Promise<NotionOAuthResponse> {
    try {
      // Get user JWT token
      const { data: { session } } = await this.supabase.auth.getSession();
      if (!session?.access_token) {
        return {
          success: false,
          error: 'Authentication required - no valid session found'
        };
      }

      const { data, error } = await this.supabase.functions.invoke('notion-oauth', {
        body: {
          action: 'callback',
          code,
          state,
        },
        headers: {
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      if (error) {
        return {
          success: false,
          error: error.message || '노션 OAuth 콜백 처리에 실패했습니다.'
        };
      }

      return {
        success: true,
        integration: data.integration,
        message: data.message,
      };
    } catch (error) {
      console.error('Error handling Notion OAuth callback:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : '노션 OAuth 콜백 처리에 실패했습니다.'
      };
    }
  }

  /**
   * 노션 연동 상태 확인
   * @deprecated 노션 연동은 이제 세션 기반으로 처리됩니다
   */
  async checkNotionIntegration(
    userId: string,
    projectId: string
  ): Promise<{ connected: boolean; integration?: NotionIntegration }> {
    // 세션 기반 OAuth로 변경되어 항상 false 반환
    return { connected: false };
  }

  /**
   * 노션 연동 해제
   * @deprecated 노션 연동은 이제 세션 기반으로 처리됩니다
   */
  async disconnectNotion(
    userId: string,
    projectId: string
  ): Promise<{ success: boolean; error?: string }> {
    // 세션 기반 OAuth로 변경되어 연동 해제가 필요 없음
    return { success: true };
  }

  /**
   * 노션 액세스 토큰 가져오기 (내부 헬퍼 메서드)
   * @deprecated 노션 연동은 이제 세션 기반으로 처리됩니다
   */
  private async getNotionAccessToken(
    userId: string,
    projectId: string
  ): Promise<string | null> {
    // 세션 기반 OAuth로 변경되어 항상 null 반환
    return null;
  }
}